import { AgGridReact } from 'ag-grid-react'; // React Data Grid Component
import { multiRowSelection } from '../agGridRowSelection';
import { themeQuartz } from 'ag-grid-community';
import { useLocation } from "react-router-dom";
import React, { useState, useEffect } from 'react'
import { Flex, Divider, View, Text, Heading, Button, SelectField, TextField } from '@aws-amplify/ui-react';
import { API, Auth } from 'aws-amplify';
import Loadingmini from '../components/loadingmini';

const DURATION_LABELS = [
    { key: 'price_1_day', label: '1 Day' },
    { key: 'price_4_day', label: '4 Day' },
    { key: 'price_1_week', label: '1 Week' },
];

function fmtMoney(val) {
    if (val === null || val === undefined || val === "" || isNaN(val)) { return "-"; }
    return "$" + Number(val).toFixed(2);
}

function fmtFactor(val) {
    if (val === null || val === undefined || val === "" || isNaN(val)) { return "-"; }
    return "x" + Number(val).toFixed(4).replace(/0+$/, '').replace(/\.$/, '.0');
}

const MAX_PRICE_FACTOR = 10.0;

function priceAtFactorOne(vehicle, durationKey, factorField) {
    const currentPrice = vehicle[durationKey];
    const raw = vehicle[factorField];
    if (currentPrice === null || currentPrice === undefined || isNaN(currentPrice)) { return null; }
    const divisor = (raw === null || raw === undefined || raw === 0) ? 1.0 : Number(raw);
    return Number(currentPrice) / divisor;
}

function implyFactorFromReference(referencePrice, targetPrice) {
    const target = parseFloat(targetPrice);
    if (referencePrice === null || referencePrice === undefined || referencePrice <= 0 || isNaN(target) || target <= 0) { return null; }
    const factor = target / referencePrice;
    if (factor <= 0 || factor > MAX_PRICE_FACTOR) { return null; }
    return factor;
}

// referencePriceForKey(durationKey) must return the rack price at factor 1.0 for the lever being edited.
function ImplyFactorFromTargetFields({ referencePriceForKey, targets, setters, onUseFactor, unchangedDescription }) {
    return (
        <>
            <Text>
                Enter the rack price you want for one duration; we solve the factor that hits that price
                while keeping {unchangedDescription} unchanged. The same factor applies to all durations
                (other durations move proportionally, not to separate dollar targets).
            </Text>
            {DURATION_LABELS.map((d, i) => {
                const targetVal = targets[i];
                const setTarget = setters[i];
                const atOne = referencePriceForKey(d.key);
                const implied = implyFactorFromReference(atOne, targetVal);
                return (
                    <Flex key={d.key} direction="column" gap="0.25rem">
                        <TextField
                            label={`Target ${d.label} price ($)`}
                            placeholder={atOne != null ? "now " + fmtMoney(atOne) + " at factor 1.0" : ""}
                            value={targetVal}
                            onInput={(e) => setTarget(e.target.value)}
                        />
                        {
                            targetVal === "" ? undefined :
                            implied == null ?
                                <Text style={{ fontSize: "0.85em" }}><i>Enter a positive dollar amount (factor must be ≤ {MAX_PRICE_FACTOR}).</i></Text>
                                :
                                <Flex direction="row" gap="0.5rem" alignItems="center" wrap="wrap">
                                    <Text style={{ fontSize: "0.9em" }}>→ {fmtFactor(implied)}</Text>
                                    <Button size="small" onClick={() => onUseFactor(String(Number(implied.toFixed(4))))}>
                                        USE THIS FACTOR
                                    </Button>
                                </Flex>
                        }
                    </Flex>
                );
            })}
        </>
    );
}

export default function Fleetpricing() {
    const location = useLocation();
    const [selected_fleet, updateSelectedFleet] = React.useState([]);
    const [is_loading, updateIsLoading] = React.useState(false);
    const [is_submitting, updateIsSubmitting] = React.useState(false);
    const [canEdit, updateCanEdit] = React.useState(false);
    const [count, setCount] = useState(0);
    const [statusMessage, updateStatusMessage] = useState("");

    const [tiers, updateTiers] = useState([]);
    const [regions, updateRegions] = useState([]);
    const [vehicles, updateVehicles] = useState([]);
    const [curveUnits, updateCurveUnits] = useState({});

    const [selectedVehicle, updateSelectedVehicle] = useState(false);

    //tier factor edit form
    const [displayTierForm, updateDisplayTierForm] = useState(false);
    const [tierFormTierNum, updateTierFormTierNum] = useState("");
    const [tierFormRegion, updateTierFormRegion] = useState("0");
    const [tierFormFactor, updateTierFormFactor] = useState("");

    //per-vehicle forms
    const [moveTierNum, updateMoveTierNum] = useState("");
    const [overrideFactor, updateOverrideFactor] = useState("");
    const [overrideNotes, updateOverrideNotes] = useState("");
    const [implyTarget1Day, updateImplyTarget1Day] = useState("");
    const [implyTarget4Day, updateImplyTarget4Day] = useState("");
    const [implyTarget1Week, updateImplyTarget1Week] = useState("");
    const [tierBucketFactor, updateTierBucketFactor] = useState("");
    const [tierImplyTarget1Day, updateTierImplyTarget1Day] = useState("");
    const [tierImplyTarget4Day, updateTierImplyTarget4Day] = useState("");
    const [tierImplyTarget1Week, updateTierImplyTarget1Week] = useState("");
    const [tierFormImplyTarget1Day, updateTierFormImplyTarget1Day] = useState("");
    const [tierFormImplyTarget4Day, updateTierFormImplyTarget4Day] = useState("");
    const [tierFormImplyTarget1Week, updateTierFormImplyTarget1Week] = useState("");

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const fleetsParam = params.get("fleets");
        if (fleetsParam) {
            updateSelectedFleet(fleetsParam.split(","));
        } else {
            updateSelectedFleet([]);
        }
    }, [location.search]);

    async function getJwtToken() {
        let retries = 5;
        let token = null;
        var accessToken = null;
        while (retries > 0) {
            try {
                const session = await Auth.currentSession();
                accessToken = session.getAccessToken()
                token = accessToken.getJwtToken()
                if (token) break; // valid token ready
            } catch (e) {
                console.log("Auth not ready, retrying...", e);
            }
            retries--;
            await new Promise(res => setTimeout(res, 500)); // wait 0.5s before retry
        }
        if (!token) throw new Error("Unable to get a valid JWT token from Auth");

        if (accessToken.payload && accessToken.payload["cognito:groups"]) {
            //support is view-only - edits restricted to admin and fleet
            if (accessToken.payload["cognito:groups"][0] === 'admin' || accessToken.payload["cognito:groups"][0] === 'fleet') {
                updateCanEdit(true)
            }
        }
        return token;
    }

    async function fetchPageData() {
        const thisFleet = selected_fleet[0] ?? "";
        if (thisFleet === "") {
            updateStatusMessage("Select a fleet to view pricing.")
            updateTiers([]); updateRegions([]); updateVehicles([]); updateCurveUnits({});
            return;
        }
        updateStatusMessage("")
        let myJwt;
        try {
            myJwt = await getJwtToken();
        } catch (e) {
            console.error("Cannot fetch page data: ", e);
            return;
        }
        updateIsLoading(true)
        const myInit = {
            queryStringParameters: {
                back_end: process.env.REACT_APP_BACKEND,
                jwt: myJwt,
                api_endpoint: "api3:staff:fleet_pricing",
                request_type: "GET",
                fleet_code: thisFleet
            }
        };
        try {
            const res = await API.get('adminWebTools', '/ago', myInit)
            updateTiers(res.tiers ?? [])
            updateRegions(res.regions ?? [])
            updateVehicles(res.vehicles ?? [])
            updateCurveUnits(res.curve_units ?? {})
            if (selectedVehicle !== false) {
                const refreshed = (res.vehicles ?? []).find(v => v.car_id === selectedVehicle.car_id)
                updateSelectedVehicle(refreshed ?? false)
            }
        }
        catch (e) {
            console.log('fleetpricing error: ' + String(e));
            updateStatusMessage("Error retrieving pricing - email us at developer@agorisk.com")
            updateTiers([]); updateRegions([]); updateVehicles([]); updateCurveUnits({});
        }
        updateIsLoading(false)
    }

    async function submitAction(params, successMessage) {
        const thisFleet = selected_fleet[0] ?? "";
        if (thisFleet === "") { return; }
        let myJwt;
        try {
            myJwt = await getJwtToken();
        } catch (e) {
            console.error("Cannot submit: ", e);
            return;
        }
        updateIsSubmitting(true)
        const myInit = {
            queryStringParameters: {
                back_end: process.env.REACT_APP_BACKEND,
                jwt: myJwt,
                api_endpoint: "api3:staff:fleet_pricing",
                request_type: "POST",
                fleet_code: thisFleet,
                ...params
            }
        };
        try {
            const res = await API.get('adminWebTools', '/ago', myInit)
            if (res && res.status === 'success') {
                updateStatusMessage(successMessage)
            } else {
                updateStatusMessage('Change failed: ' + JSON.stringify(res))
            }
        }
        catch (e) {
            console.log('fleetpricing POST error: ' + String(e));
            var detail = ""
            try { detail = e.response.data.detail } catch (e2) { detail = String(e) }
            updateStatusMessage('Change failed: ' + detail)
        }
        updateIsSubmitting(false)
        setCount(count + 1)
    }

    function submitTierFactor() {
        if (tierFormTierNum === "" || tierFormFactor === "") {
            updateStatusMessage("Select a tier and enter a factor.")
            return;
        }
        submitAction({
            action: "set_tier_factor",
            tier_num: parseInt(tierFormTierNum),
            region: parseInt(tierFormRegion),
            price_factor: parseFloat(tierFormFactor)
        }, "Tier factor updated.")
        updateDisplayTierForm(false)
        updateTierFormFactor("")
        updateTierFormImplyTarget1Day("")
        updateTierFormImplyTarget4Day("")
        updateTierFormImplyTarget1Week("")
    }

    function submitMoveTier() {
        if (selectedVehicle === false || moveTierNum === "") { return; }
        submitAction({
            action: "set_vehicle_tier",
            vehicle_id: selectedVehicle.car_id,
            tier_num: parseInt(moveTierNum)
        }, "Vehicle tier updated.")
        updateMoveTierNum("")
    }

    function submitOverride() {
        if (selectedVehicle === false || overrideFactor === "") { return; }
        submitAction({
            action: "set_vehicle_factor",
            vehicle_id: selectedVehicle.car_id,
            factor: parseFloat(overrideFactor),
            notes: overrideNotes
        }, "Vehicle price override updated.")
        updateOverrideFactor("")
        updateOverrideNotes("")
        updateImplyTarget1Day("")
        updateImplyTarget4Day("")
        updateImplyTarget1Week("")
    }

    function submitTierBucketForVehicle() {
        if (selectedVehicle === false || tierBucketFactor === "") { return; }
        if (!selectedVehicle.tier_num || selectedVehicle.tier_num <= 0) {
            updateStatusMessage("Assign this vehicle to a tier first (Move to Tier), then set that tier's bucket multiplier.")
            return;
        }
        const region = selectedVehicle.region_id != null ? selectedVehicle.region_id : 0;
        submitAction({
            action: "set_tier_factor",
            tier_num: selectedVehicle.tier_num,
            region: region,
            price_factor: parseFloat(tierBucketFactor)
        }, "Tier bucket factor updated for " + tierDisplayName(selectedVehicle.tier_num) + " (region " + region + "). Applies to all vehicles in this tier.")
        updateTierBucketFactor("")
        updateTierImplyTarget1Day("")
        updateTierImplyTarget4Day("")
        updateTierImplyTarget1Week("")
    }

    useEffect(() => {
        fetchPageData()
    }, [count, selected_fleet])

    //distinct tier catalog (fleet row preferred over global for display)
    const tierCatalog = [];
    {
        const seen = {};
        const sorted = [...tiers].sort((a, b) => (b.fleet_id - a.fleet_id) || (a.region - b.region));
        for (const t of sorted) {
            if (!(t.tier_num in seen)) {
                seen[t.tier_num] = true;
                tierCatalog.push(t);
            }
        }
        tierCatalog.sort((a, b) => (a.sort_order - b.sort_order) || (a.tier_num - b.tier_num));
    }

    function tierDisplayName(tierNum) {
        if (tierNum === null || tierNum === undefined || tierNum === 0) { return "-"; }
        const t = tierCatalog.find(t => t.tier_num === tierNum);
        if (!t) { return String(tierNum); }
        return tierNum + " - " + (t.display_name || t.label);
    }

    const tierColumns = [
        { headerName: 'Scope', valueGetter: p => p.data.fleet_id === 0 ? 'Global default' : 'This fleet', filter: true },
        { headerName: 'Region', field: 'region', filter: true, width: 110 },
        { headerName: 'Tier #', field: 'tier_num', filter: true, width: 100 },
        { headerName: 'Code', field: 'label', filter: true },
        { headerName: 'Name', field: 'display_name', filter: true },
        { headerName: 'Price Factor', field: 'price_factor', valueFormatter: p => fmtFactor(p.value) },
        { headerName: 'Deposit Factor', field: 'deposit_factor', valueFormatter: p => fmtFactor(p.value) },
    ];

    const regionColumns = [
        { headerName: 'Region', field: 'region', width: 110 },
        { headerName: 'Name', valueGetter: p => p.data.region_name || (p.data.region === 0 ? 'Default' : ''), filter: true },
        { headerName: 'Base Hourly Price', field: 'price', valueFormatter: p => fmtMoney(p.value) },
        { headerName: 'Deposit', field: 'deposit', valueFormatter: p => fmtMoney(p.value) },
    ];

    const vehicleColumns = [
        { headerName: 'ID', field: 'car_id', width: 90, filter: true },
        { headerName: 'Name', field: 'name', filter: true },
        { headerName: 'VIN', field: 'vin', filter: true },
        { headerName: 'Vehicle', field: 'make_model_year', filter: true },
        { headerName: 'Region', field: 'region', filter: true },
        { headerName: 'Status', field: 'vehicle_status', filter: true },
        { headerName: 'Tier', valueGetter: p => tierDisplayName(p.data.tier_num), filter: true },
        { headerName: 'Base $/hr', field: 'base_hourly', valueFormatter: p => fmtMoney(p.value), width: 110 },
        { headerName: 'Model', field: 'model_factor', valueFormatter: p => fmtFactor(p.value), width: 100 },
        { headerName: 'Year', field: 'year_factor', valueFormatter: p => fmtFactor(p.value), width: 100 },
        { headerName: 'Tier Factor', field: 'tier_factor', valueFormatter: p => fmtFactor(p.value), width: 110 },
        { headerName: 'VIN Override', field: 'vehicle_factor', valueFormatter: p => fmtFactor(p.value), width: 120 },
        { headerName: '1 Day', field: 'price_1_day', valueFormatter: p => fmtMoney(p.value), width: 110 },
        { headerName: '4 Day', field: 'price_4_day', valueFormatter: p => fmtMoney(p.value), width: 110 },
        { headerName: '1 Week', field: 'price_1_week', valueFormatter: p => fmtMoney(p.value), width: 110 },
    ];

    const factorsProduct = selectedVehicle === false ? 1.0 :
        (selectedVehicle.model_factor ?? 1.0) * (selectedVehicle.year_factor ?? 1.0) *
        (selectedVehicle.tier_factor ?? 1.0) * (selectedVehicle.vehicle_factor ?? 1.0);

    //region base hourly for the tier form's target-price inference (engine falls back to region 0)
    const regionPriceMap = {};
    for (const r of regions) { if (r.price != null) { regionPriceMap[r.region] = Number(r.price); } }
    const tierFormBasePrice = regionPriceMap[parseInt(tierFormRegion)] ?? regionPriceMap[0] ?? null;

    return (
        <Flex direction="column" style={{ width: "100%" }}>
            <View style={{ padding: "1rem" }}>
                {
                    is_loading ? <Loadingmini /> :
                        <Flex direction='column'>
                            <Heading level={1}>Pricing</Heading>
                            <Text style={{ width: '100%' }}>
                                Rack-rate prices per vehicle (default driver scores) and the factors that go into them.
                                Click a vehicle row to see the full breakdown{canEdit ? " and make adjustments" : ""}.
                            </Text>
                            {statusMessage === "" ? undefined : <Text style={{ fontWeight: "bold" }}>{statusMessage}</Text>}
                            <View>
                                <Button onClick={() => setCount(count + 1)}>
                                    PRESS TO GET LATEST UPDATES
                                </Button>
                            </View>
                            <Divider />

                            <Heading level={3}>Region Base Prices</Heading>
                            <Text><i>Read only - region pricing changes are pending a redesign.</i></Text>
                            <View theme={themeQuartz} style={{ height: 120, width: "100%" }}>
                                <AgGridReact
                                    rowData={regions}
                                    columnDefs={regionColumns}
                                    enableCellTextSelection={true}
                                />
                            </View>
                            <Divider />

                            <Heading level={3}>Pricing Tiers</Heading>
                            <Text>Tier factors multiply the base price for every vehicle in the tier. Fleet rows override global defaults; the pricing engine only applies rows scoped to this fleet.</Text>
                            <View theme={themeQuartz} style={{ height: 250, width: "100%" }}>
                                <AgGridReact
                                    rowData={tiers}
                                    columnDefs={tierColumns}
                                    enableCellTextSelection={true}
                                />
                            </View>
                            {
                                canEdit === false ? undefined :
                                    displayTierForm === false ?
                                        <View>
                                            <Button onClick={() => updateDisplayTierForm(true)}>EDIT TIER PRICE FACTOR</Button>
                                        </View>
                                        :
                                        <Flex direction="column" gap="1rem">
                                            <Heading level={4}>Edit Tier Price Factor</Heading>
                                            <Text>Sets the price factor for this fleet (creates a fleet-specific tier row if only a global default exists).</Text>
                                            <SelectField
                                                label="Tier"
                                                value={tierFormTierNum}
                                                onChange={(e) => updateTierFormTierNum(e.target.value)}
                                            >
                                                <option value="">select a tier</option>
                                                {tierCatalog.map(t => <option key={t.tier_num} value={t.tier_num}>{tierDisplayName(t.tier_num)}</option>)}
                                            </SelectField>
                                            <SelectField
                                                label="Region"
                                                value={tierFormRegion}
                                                onChange={(e) => updateTierFormRegion(e.target.value)}
                                            >
                                                <option value="0">0 (fleet default)</option>
                                                {regions.filter(r => r.region !== 0).map(r => <option key={r.region} value={r.region}>{r.region}{r.region_name ? " - " + r.region_name : ""}</option>)}
                                            </SelectField>
                                            {
                                                tierFormTierNum === "" ?
                                                    <Text style={{ fontWeight: "bold" }}>Select a tier above to continue — the multiplier (typed or inferred from a target price) will be applied to that tier for the selected region.</Text>
                                                    :
                                                    <>
                                                        <Text style={{ fontWeight: "bold" }}>Editing: {tierDisplayName(parseInt(tierFormTierNum))}, region {tierFormRegion}. This changes the price for all vehicles in this tier.</Text>
                                                        <Heading level={5}>Infer bucket multiplier from target price</Heading>
                                                        <Text><i>Reference is a typical vehicle in the selected region (model, year, and VIN factors of 1.0); works even if the tier is empty. Cars with other factors scale proportionally.</i></Text>
                                                        <ImplyFactorFromTargetFields
                                                            referencePriceForKey={(key) => (tierFormBasePrice != null && curveUnits[key] != null) ? tierFormBasePrice * Number(curveUnits[key]) : null}
                                                            targets={[tierFormImplyTarget1Day, tierFormImplyTarget4Day, tierFormImplyTarget1Week]}
                                                            setters={[updateTierFormImplyTarget1Day, updateTierFormImplyTarget4Day, updateTierFormImplyTarget1Week]}
                                                            onUseFactor={updateTierFormFactor}
                                                            unchangedDescription="region base, model, year, and VIN override"
                                                        />
                                                        <Divider />
                                                        <TextField
                                                            label={`Tier bucket multiplier for ${tierDisplayName(parseInt(tierFormTierNum))} (e.g. 1.25)`}
                                                            placeholder="1.0"
                                                            value={tierFormFactor}
                                                            onInput={(e) => updateTierFormFactor(e.target.value)}
                                                        />
                                                    </>
                                            }
                                            <Button isDisabled={is_submitting || tierFormTierNum === "" || tierFormFactor === ""} onClick={() => submitTierFactor()}>
                                                {tierFormTierNum === "" ? "SUBMIT (select a tier first)" : `SUBMIT — UPDATE ${tierDisplayName(parseInt(tierFormTierNum)).toUpperCase()}`}
                                            </Button>
                                            <Button onClick={() => updateDisplayTierForm(false)}>CANCEL</Button>
                                        </Flex>
                            }
                            <Divider />

                            <Heading level={3}>Vehicles</Heading>
                            <Text style={{ fontWeight: "bold", fontSize: "1.1em" }}>Click a vehicle row to see its full price breakdown{canEdit ? " and adjustment controls" : ""} below the table.</Text>
                            <Text><i>Hint: To search, long-click on mobile or click top-right on pc.</i></Text>
                            <View theme={themeQuartz} style={{ height: 250, width: "100%" }}>
                                <AgGridReact
                                    rowData={vehicles}
                                    columnDefs={vehicleColumns}
                                    rowSelection={multiRowSelection}
                                    paginationPageSize={500}
                                    paginationPageSizeSelector={[10, 25, 50]}
                                    enableCellTextSelection={true}
                                    onRowClicked={(e) => {
                                        updateSelectedVehicle(e.data)
                                        updateMoveTierNum("")
                                        updateOverrideFactor("")
                                        updateOverrideNotes("")
                                        updateImplyTarget1Day("")
                                        updateImplyTarget4Day("")
                                        updateImplyTarget1Week("")
                                        updateTierBucketFactor("")
                                        updateTierImplyTarget1Day("")
                                        updateTierImplyTarget4Day("")
                                        updateTierImplyTarget1Week("")
                                    }}
                                />
                            </View>

                            {
                                selectedVehicle === false ? undefined :
                                    <Flex direction="column" gap="1rem">
                                        <Divider />
                                        <Heading level={3}>{selectedVehicle.make_model_year} - {selectedVehicle.vin}</Heading>
                                        <Heading level={4}>Price Breakdown</Heading>
                                        <Text>
                                            Rack price = (region base hourly × duration curve) × model factor × year factor × tier factor × VIN override factor.
                                        </Text>
                                        <Text style={{ fontWeight: "bold" }}>
                                            Tier bucket and VIN override are both extra multipliers on top of region base, model, and year — neither replaces the other.
                                            At factor 1.0 there is no adjustment for that lever; changing one leaves the others in place.
                                        </Text>
                                        <Text>
                                            Driver-facing quotes can still differ from rack rate (risk, trust, and driving-behavior scores).
                                        </Text>
                                        <table style={{ borderCollapse: "collapse", maxWidth: 700 }}>
                                            <thead>
                                                <tr style={{ textAlign: "left", borderBottom: "1px solid #ccc" }}>
                                                    <th style={{ padding: "0.25rem 1rem 0.25rem 0" }}>Duration</th>
                                                    <th style={{ padding: "0.25rem 1rem 0.25rem 0" }}>Base $/hr</th>
                                                    <th style={{ padding: "0.25rem 1rem 0.25rem 0" }}>Curve Hours</th>
                                                    <th style={{ padding: "0.25rem 1rem 0.25rem 0" }}>Factors</th>
                                                    <th style={{ padding: "0.25rem 1rem 0.25rem 0" }}>Price</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {DURATION_LABELS.map(d =>
                                                    <tr key={d.key} style={{ borderBottom: "1px solid #eee" }}>
                                                        <td style={{ padding: "0.25rem 1rem 0.25rem 0" }}>{d.label}</td>
                                                        <td style={{ padding: "0.25rem 1rem 0.25rem 0" }}>{fmtMoney(selectedVehicle.base_hourly)}</td>
                                                        <td style={{ padding: "0.25rem 1rem 0.25rem 0" }}>{curveUnits[d.key] === undefined ? "-" : Number(curveUnits[d.key]).toFixed(2)}</td>
                                                        <td style={{ padding: "0.25rem 1rem 0.25rem 0" }}>
                                                            {fmtFactor(selectedVehicle.model_factor)} model, {fmtFactor(selectedVehicle.year_factor)} year, {fmtFactor(selectedVehicle.tier_factor)} tier, {fmtFactor(selectedVehicle.vehicle_factor)} VIN = {fmtFactor(factorsProduct)}
                                                        </td>
                                                        <td style={{ padding: "0.25rem 1rem 0.25rem 0", fontWeight: "bold" }}>{fmtMoney(selectedVehicle[d.key])}</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                        {
                                            selectedVehicle.vehicle_factor_notes ?
                                                <Text><i>VIN override note ({selectedVehicle.vehicle_factor_created}): {selectedVehicle.vehicle_factor_notes}</i></Text>
                                                : undefined
                                        }
                                        {
                                            canEdit === false ? undefined :
                                                <Flex direction="row" gap="3rem" wrap="wrap">
                                                    <Flex direction="column" gap="1rem" style={{ minWidth: 280 }}>
                                                        <Heading level={4}>Move to Tier</Heading>
                                                        <Text>Current tier: {tierDisplayName(selectedVehicle.tier_num)}</Text>
                                                        <SelectField
                                                            label="New tier"
                                                            value={moveTierNum}
                                                            onChange={(e) => updateMoveTierNum(e.target.value)}
                                                        >
                                                            <option value="">select a tier</option>
                                                            <option value="0">0 - none (no tier factor)</option>
                                                            {tierCatalog.map(t => <option key={t.tier_num} value={t.tier_num}>{tierDisplayName(t.tier_num)}</option>)}
                                                        </SelectField>
                                                        <Button isDisabled={is_submitting || moveTierNum === ""} onClick={() => submitMoveTier()}>MOVE VEHICLE</Button>
                                                    </Flex>
                                                    <Flex direction="column" gap="1rem" style={{ minWidth: 320, maxWidth: 420 }}>
                                                        <Heading level={4}>Tier Bucket Multiplier</Heading>
                                                        {
                                                            !selectedVehicle.tier_num || selectedVehicle.tier_num <= 0 ?
                                                                <Text>Vehicle has no tier (multiplier 1.0). Use Move to Tier first, or Edit Tier Price Factor above for a specific bucket.</Text>
                                                                :
                                                                <>
                                                                    <Text>
                                                                        Current tier: {tierDisplayName(selectedVehicle.tier_num)} — bucket multiplier {fmtFactor(selectedVehicle.tier_factor)}.
                                                                        Changing this updates the tier bucket for all vehicles in tier {selectedVehicle.tier_num} (region {selectedVehicle.region_id ?? 0}), not just this VIN.
                                                                    </Text>
                                                                    <Heading level={5}>Infer bucket multiplier from target price</Heading>
                                                                    <ImplyFactorFromTargetFields
                                                                        referencePriceForKey={(key) => priceAtFactorOne(selectedVehicle, key, 'tier_factor')}
                                                                        targets={[tierImplyTarget1Day, tierImplyTarget4Day, tierImplyTarget1Week]}
                                                                        setters={[updateTierImplyTarget1Day, updateTierImplyTarget4Day, updateTierImplyTarget1Week]}
                                                                        onUseFactor={updateTierBucketFactor}
                                                                        unchangedDescription="region base, model, year, and VIN override"
                                                                    />
                                                                    <Divider />
                                                                    <TextField
                                                                        label="Tier bucket multiplier"
                                                                        placeholder="1.0"
                                                                        value={tierBucketFactor}
                                                                        onInput={(e) => updateTierBucketFactor(e.target.value)}
                                                                    />
                                                                    <Button isDisabled={is_submitting || tierBucketFactor === ""} onClick={() => submitTierBucketForVehicle()}>SET TIER MULTIPLIER</Button>
                                                                </>
                                                        }
                                                    </Flex>
                                                    <Flex direction="column" gap="1rem" style={{ minWidth: 320, maxWidth: 420 }}>
                                                        <Heading level={4}>VIN Price Override</Heading>
                                                        <Text>Current override: {fmtFactor(selectedVehicle.vehicle_factor)}. Set factor to 1.0 to clear (tier/model/year still apply).</Text>

                                                        <Heading level={5}>Infer factor from target price</Heading>
                                                        <ImplyFactorFromTargetFields
                                                            referencePriceForKey={(key) => priceAtFactorOne(selectedVehicle, key, 'vehicle_factor')}
                                                            targets={[implyTarget1Day, implyTarget4Day, implyTarget1Week]}
                                                            setters={[updateImplyTarget1Day, updateImplyTarget4Day, updateImplyTarget1Week]}
                                                            onUseFactor={updateOverrideFactor}
                                                            unchangedDescription="region base, tier, model, and year"
                                                        />

                                                        <Divider />
                                                        <TextField
                                                            label="Override factor (e.g. 0.9 = 10% cheaper than tier/model/year)"
                                                            placeholder="1.0"
                                                            value={overrideFactor}
                                                            onInput={(e) => updateOverrideFactor(e.target.value)}
                                                        />
                                                        <TextField
                                                            label="Reason"
                                                            placeholder="notes on why"
                                                            value={overrideNotes}
                                                            onInput={(e) => updateOverrideNotes(e.target.value)}
                                                        />
                                                        <Button isDisabled={is_submitting || overrideFactor === ""} onClick={() => submitOverride()}>SET OVERRIDE</Button>
                                                    </Flex>
                                                </Flex>
                                        }
                                    </Flex>
                            }
                            <Divider />
                        </Flex>
                }
            </View>
        </Flex>
    );
}
